import React, { useEffect } from "react";
import { BadgeStatus, SaleOrder } from "@/types/general";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import GeneralSection from "./parts/GeneralSection";
import ReferencesSection from "./parts/ReferencesSection";
import AddressSection from "./parts/AddressSection";
import ProductSection from "./parts/ProductSection";
import dayjs from "dayjs";
import {
    LineSchema,
    ReservationSchema,
    SaleOrdersFormTyping,
    saleOrdersScheme,
    SaleOrderSubmitIntent,
} from "./types";
import LanguageSection from "./parts/LanguageSection";
import ReservationsSection from "./parts/ReservationsSection";
import { toReservationAllocation } from "./parts/ReservationsSection/utils";
import { cn, normalizePrice } from "@helpers/utils";
import { buildProductMapFromLines } from "@components/Dashboard/pages/Sales/Orders/components/ViewLayout/ViewReservationsSection";
import UIButton from "@reusables/UIButton";
import { useTranslation } from "react-i18next";
import { useIsReservationsEnabled } from "@redux/features/companies/useIsReservationsEnabled";
import IntegrationsSection from "./parts/IntegrationsSection";
import OfferActivationModal from "@components/Dashboard/pages/Sales/Orders/components/modals/OfferActivationModal";
import OrderReference from "./parts/OrderReference";
import CommentSection from "./parts/CommentSection";
import { CopyHydrationProvider } from "./CopyHydrationContext";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import HeaderStatusControl from "./parts/HeaderStatusControl";
import CreateOrderSubmitActions from "./parts/CreateOrderSubmitActions";
import CurrencySection from "./parts/CurrencySection";
import { OrderCurrencyProvider, useBaseCurrency } from "../OrderCurrencyContext";

/**
 * Saved order lines expose the last purchase price as `cost_price`, while the product endpoints
 * feeding new lines (and therefore the form) call it `purchase_price`.
 */
function toFormLineProduct(
    product: SaleOrder.Components.LineData["product"]
): LineSchema["product"] {
    return {
        ...product,
        prices: {
            selling_price: product.prices.selling_price,
            purchase_price: product.prices.cost_price,
        },
    };
}

interface MutationLayoutProperties {
    saleOrder: SaleOrder.Extended | undefined;
    copiedOrder?: SaleOrder.Extended;
    onSubmit: (args: SaleOrdersFormTyping & { saveAndPick: boolean }) => void;
    disabled?: boolean;
}

export default function MutationLayout(props: MutationLayoutProperties) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders" });
    const isEditingMutation = !!props.saleOrder;

    // Reading reservations feature flag to keep section rendering aligned with company settings.
    const isReservationsEnabled = useIsReservationsEnabled();

    // New orders and orders saved before currencies were supported are in the company base currency
    const baseCurrency = useBaseCurrency();

    const form = useForm<SaleOrdersFormTyping>({
        resolver: zodResolver(saleOrdersScheme),
        defaultValues: {
            mode: "creation",
            status: BadgeStatus.Base.new,
            is_editable: true,
            is_billing_for_delivery: false,
            order_date: dayjs(),
            currency: baseCurrency,
            reference_number: {},
            their_reference_name: null,
            comment: undefined,
            lines: [],
            is_invoiced: false,
            deleted_lines: [],
        },
    });

    const buildSaleOrderLines = React.useCallback(
        (order: SaleOrder.Extended, isCopy: boolean): SaleOrdersFormTyping["lines"] =>
            order.lines.map((line) => {
                if (line.type === "line") {
                    return {
                        id: isCopy ? undefined : line.data.id,
                        comment: line.data.comment,
                        product: toFormLineProduct(line.data.product),
                        quantity: line.data.quantity,
                        unit_price: normalizePrice(line.data.unit_price),
                        discount: line.data.discount,
                        tax: line.data.tax,
                        ...(line.data.alternative_name && {
                            alternative_name: line.data.alternative_name,
                        }),
                    };
                }

                return {
                    id: isCopy ? undefined : line.data.id,
                    name: line.data.name,
                    comment: line.data.comment,
                    module: line.data.module,
                    module_entity_id: line.data.module_entity_id,
                    lines: line.data.lines.map((groupLine) => ({
                        id: isCopy ? undefined : groupLine.data.id,
                        comment: groupLine.data.comment,
                        product: toFormLineProduct(groupLine.data.product),
                        quantity: groupLine.data.quantity,
                        unit_price: normalizePrice(groupLine.data.unit_price),
                        discount: groupLine.data.discount,
                        tax: groupLine.data.tax,
                        ...(groupLine.data.alternative_name && {
                            alternative_name: groupLine.data.alternative_name,
                        }),
                    })),
                };
            }),
        []
    );

    const hydrateSaleOrder = React.useCallback(
        (order: SaleOrder.Extended, isCopy: boolean) => {
            form.setValue("mode", isCopy ? "creation" : "editing");
            form.setValue("is_editable", isCopy ? true : order.is_editable);
            form.setValue(
                "status",
                isCopy
                    ? BadgeStatus.Base.new
                    : order.status.name === BadgeStatus.Base.offer ||
                      order.status.name === BadgeStatus.Base.prepaid
                    ? order.status.name
                    : BadgeStatus.Base.new
            );

            form.setValue("customer", order.customer);
            form.setValue("order_date", isCopy ? dayjs() : order.order_date);
            form.setValue(
                "preferred_delivery_date",
                isCopy ? undefined : order.preferred_delivery_date
            );
            form.setValue("our_reference", order.our_reference);
            form.setValue("their_reference", order.their_reference);
            form.setValue(
                "their_reference_name",
                order.their_reference ? null : order.their_reference_name ?? null
            );
            form.setValue("reference_number", isCopy ? {} : order.reference_number ?? {});
            form.setValue("payment_terms", order.payment_terms);
            form.setValue("delivery_terms", order.delivery_terms);
            form.setValue("language", order.language);
            form.setValue("currency", order.currency ?? baseCurrency);
            form.setValue("comment", order.comment);

            if (order.billing_address) {
                form.setValue("billing_address", {
                    name: order.billing_address.name,
                    street: order.billing_address.street,
                    city: order.billing_address.city,
                    country: order.billing_address.country,
                });
            }

            form.setValue("delivery_address", order.delivery_address);
            form.setValue("is_billing_for_delivery", order.is_billing_for_delivery);
            form.setValue("is_invoiced", isCopy ? false : order.is_invoiced);

            if (order.project) {
                form.setValue("twenty_four_seven_office.project", order.project);
            }

            form.setValue("lines", buildSaleOrderLines(order, isCopy));
            form.setValue("deleted_lines", []);

            if (isCopy) {
                form.setValue("reservations", []);
                form.setValue("reservations_expiration_date", null);
                return;
            }

            if (order.reservations?.length > 0) {
                const productMap = buildProductMapFromLines(order.lines);

                // Backend returns a reservation record per location, while the form keeps a single entry per product
                const reservationsByProduct = new Map<number, ReservationSchema>();

                order.reservations.forEach((reservation) => {
                    const product = productMap.get(reservation.product_id);
                    if (!product) return;

                    const allocation = toReservationAllocation(reservation);
                    const existing = reservationsByProduct.get(product.id);

                    if (existing) {
                        existing.allocations.push(allocation);
                    } else {
                        reservationsByProduct.set(product.id, { product, allocations: [allocation] });
                    }
                });

                form.setValue("reservations", Array.from(reservationsByProduct.values()));
            }

            if (order.reservation_expires_at) {
                form.setValue("reservations_expiration_date", dayjs(order.reservation_expires_at));
            }
        },
        [baseCurrency, buildSaleOrderLines, form]
    );

    const submitIntentRef = React.useRef<SaleOrderSubmitIntent>(
        isEditingMutation ? "edit_save" : "save_order"
    );
    // Controlling submit-time Offer -> New confirmation modal visibility.
    const [isOfferActivationModalOpen, setIsOfferActivationModalOpen] = React.useState(false);

    // Holding pending submit payload without triggering rerenders while modal is opening/closing.
    const pendingSubmitDataRef = React.useRef<
        (SaleOrdersFormTyping & { saveAndPick: boolean }) | null
    >(null);

    React.useEffect(() => {
        submitIntentRef.current = isEditingMutation ? "edit_save" : "save_order";
    }, [isEditingMutation]);

    useEffect(() => {
        if (props.saleOrder) {
            hydrateSaleOrder(props.saleOrder, false);
        }
    }, [hydrateSaleOrder, props.saleOrder]);

    useEffect(() => {
        if (props.copiedOrder) {
            hydrateSaleOrder(props.copiedOrder, true);
        }
    }, [hydrateSaleOrder, props.copiedOrder]);

    const onSubmit = form.handleSubmit((data: SaleOrdersFormTyping) => {
        const submitIntent =
            submitIntentRef.current ?? (isEditingMutation ? "edit_save" : "save_order");
        const resolvedStatus =
            submitIntent === "save_offer"
                ? BadgeStatus.Base.offer
                : submitIntent === "save_prepaid"
                ? BadgeStatus.Base.prepaid
                : submitIntent === "edit_save"
                ? data.status
                : BadgeStatus.Base.new;
        const resolvedSaveAndPick = submitIntent === "save_and_pick";

        // The submit intent translates the footer choice into the form payload, which keeps
        // page-level mutation code unchanged and makes create-mode buttons the source of truth
        // for whether the next flow should be an offer, a saved order, or a picking redirect.
        const submitData = {
            ...data,
            status: resolvedStatus,
            saveAndPick: resolvedSaveAndPick,
        };

        // Unused, because we forbid editing SO if it's invoiced
        // if (props.saleOrder?.is_invoiced) {
        //     for (let i = 0; i < form.getValues("lines").length; i++) {
        //         const originalQuantity = props.saleOrder?.lines[i].data?.quantity;
        //         const newQuantity = form.getValues("lines")[i].data!.quantity;

        //         // If the new quantity is less than the original, show error and stop the mutation
        //         if (newQuantity < originalQuantity) {
        //             toast.error(`Quantity for product ${form.getValues("lines")[i].data?.product.name} cannot be less than the original quantity.`);
        //             return;
        //         }
        //     }
        // }

        const shouldConfirmOfferActivationOnSave =
            props.saleOrder?.status.name === BadgeStatus.Base.offer &&
            data.mode === "editing" &&
            submitData.status === BadgeStatus.Base.new;

        // The confirmation modal only intercepts a real Offer -> New transition, so Save as offer
        // and unchanged Offer edits continue through the normal submit path without extra prompts.
        if (shouldConfirmOfferActivationOnSave) {
            pendingSubmitDataRef.current = submitData;
            setIsOfferActivationModalOpen(true);
            return;
        }

        props.onSubmit(submitData);
    }, console.error);

    const submitWithIntent = React.useCallback(
        (intent: SaleOrderSubmitIntent) => {
            submitIntentRef.current = intent;
            onSubmit();
        },
        // The shared submit callback stays centralized so creation and editing keep the same
        // validation path even though their save triggers now express different intents.
        [onSubmit]
    );

    const copyHydrationValue = React.useMemo(
        () => ({
            isCopiedDraft: !!props.copiedOrder,
            copiedEntityId: props.copiedOrder?.customer.id,
            copiedEntityType: props.copiedOrder ? ("customer" as const) : null,
        }),
        [props.copiedOrder]
    );

    return (
        <CopyHydrationProvider value={copyHydrationValue}>
            <FormProvider {...form}>
                <FormOrderCurrencyProvider saleOrder={props.saleOrder}>
                    <OrderReference />
                    <HeaderStatusControl initialStatus={props.saleOrder?.status.name} />
                    <div className={cn("space-y-[32px]", props.disabled && "hidden")}>
                        <BaseInputsGrid cols={3}>
                            <GeneralSection />
                            <ReferencesSection />
                            <LanguageSection />
                            <CurrencySection />
                        </BaseInputsGrid>
                        <BaseInputsGrid cols={3}>
                            <AddressSection />
                            <IntegrationsSection />
                        </BaseInputsGrid>
                        <CommentSection />
                        <ProductSection />
                        {/* Reservations section - only shown when feature is enabled */}
                        {isReservationsEnabled && <ReservationsSection />}
                        {isEditingMutation ? (
                            <div className="flex justify-center space-x-2">
                                <UIButton
                                    onClick={() => submitWithIntent("edit_save")}
                                    size={"lg"}
                                    className="min-w-[200px]"
                                >
                                    <span>{t("general.buttons.save")}</span>
                                </UIButton>
                            </div>
                        ) : (
                            <CreateOrderSubmitActions onSubmitIntent={submitWithIntent} />
                        )}
                        <OfferActivationModalWatcher
                            isOpen={isOfferActivationModalOpen}
                            onClose={() => {
                                pendingSubmitDataRef.current = null;
                                setIsOfferActivationModalOpen(false);
                            }}
                            onConfirm={() => {
                                const pendingSubmitData = pendingSubmitDataRef.current;
                                if (!pendingSubmitData) return;

                                props.onSubmit(pendingSubmitData);
                                pendingSubmitDataRef.current = null;
                                setIsOfferActivationModalOpen(false);
                            }}
                        />
                    </div>
                </FormOrderCurrencyProvider>
            </FormProvider>
        </CopyHydrationProvider>
    );
}

/**
 * Provides the order currency to the editor from the form. A saved order keeps converting at its
 * own exchange rate, which follows today's rate until it gets locked, while a new order converts
 * at today's rate of the selected currency.
 */
function FormOrderCurrencyProvider({
    saleOrder,
    children,
}: React.PropsWithChildren<{ saleOrder: SaleOrder.Extended | undefined }>) {
    const { control } = useFormContext<SaleOrdersFormTyping>();
    const currency = useWatch({ control, name: "currency" });

    const orderCurrency = React.useMemo(
        () => ({
            currency,
            rate: saleOrder ? saleOrder.exchange_rate : currency.rate,
            isRateLocked: saleOrder?.exchange_rate_locked ?? false,
        }),
        [currency, saleOrder]
    );

    return <OrderCurrencyProvider value={orderCurrency}>{children}</OrderCurrencyProvider>;
}

interface OfferActivationModalWatcherProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

function OfferActivationModalWatcher(props: OfferActivationModalWatcherProps) {
    const { control } = useFormContext<SaleOrdersFormTyping>();

    // Isolating modal field subscriptions so only this wrapper is rerendering on related changes.
    const customer = useWatch({ control, name: "customer" });
    const paymentTerms = useWatch({ control, name: "payment_terms" });
    const deliveryTerms = useWatch({ control, name: "delivery_terms" });

    return (
        <OfferActivationModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            onConfirm={props.onConfirm}
            customer={customer?.name}
            paymentTerms={paymentTerms?.name}
            deliveryTerms={deliveryTerms?.name}
        />
    );
}
